import type { UserRole } from '@coreltb/shared/constants';
import {
  GREETINGS,
  GREETINGS_FOR,
  NAME_FORMS,
  NICKNAMES,
  WELCOME_MESSAGES,
  type WelcomeMessage,
} from './messages';

/** Treść i pamięć codziennego powitania (bez Reacta). */
const STORAGE_KEY = 'coreltb.dailyWelcome.v1';
/**
 * Ostatnio zalogowana osoba na tym urządzeniu — żeby powitanie z imieniem było
 * od pierwszej klatki, zanim serwer potwierdzi sesję (Dawid 2026-09-30: „najpierw
 * pół sekundy zwykłe ładowanie, potem przeskok — nie może tak być”).
 */
const LAST_USER_KEY = 'coreltb.lastUser.v1';
/** Ile ostatnich haseł nie może się powtórzyć. */
const NO_REPEAT = 20;
/** Osoba z własnymi hasłami (np. Tomek) dostaje „swoje” w większości dni. */
const PERSONAL_CHANCE = 0.6;

export interface WelcomeUser {
  id: string;
  name: string;
  email: string;
  role: UserRole;
}

export interface Welcome {
  userId: string;
  greeting: string;
  message: string | null;
  messageIndex: number | null;
}

interface Stored {
  day: string;
  userId: string;
  recent: number[];
}

export function today(): string {
  const d = new Date();
  return `${d.getFullYear()}-${d.getMonth() + 1}-${d.getDate()}`;
}

function readJson<T>(key: string): T | null {
  try {
    const raw = window.localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : null;
  } catch {
    return null;
  }
}

function writeJson(key: string, value: unknown): void {
  try {
    window.localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // localStorage zablokowany — powitanie pokaże się przy każdym otwarciu, trudno.
  }
}

export function welcomedToday(userId: string, day: string): boolean {
  const stored = readJson<Stored>(STORAGE_KEY);
  return Boolean(stored && stored.day === day && stored.userId === userId);
}

export function recordWelcome(welcome: Welcome, day: string): void {
  const recent = readJson<Stored>(STORAGE_KEY)?.recent ?? [];
  const next =
    welcome.messageIndex === null ? recent : [welcome.messageIndex, ...recent].slice(0, NO_REPEAT);
  writeJson(STORAGE_KEY, { day, userId: welcome.userId, recent: next });
}

export function readLastUser(): WelcomeUser | null {
  const u = readJson<Partial<WelcomeUser>>(LAST_USER_KEY);
  if (!u?.id || !u.name || !u.email || !u.role) return null;
  return { id: u.id, name: u.name, email: u.email, role: u.role };
}

export function rememberUser({ id, name, email, role }: WelcomeUser): void {
  writeJson(LAST_USER_KEY, { id, name, email, role });
}

function pick<T>(items: readonly T[]): T | undefined {
  return items[Math.floor(Math.random() * items.length)];
}

/** Imię do powitania albo null, gdy w koncie siedzi tylko login z e-maila. */
function displayName(user: { name: string; email: string }): string | null {
  const mail = user.email.toLowerCase();
  const fixed = NICKNAMES[mail];
  if (fixed) return fixed;
  const first = user.name.trim().split(/\s+/)[0] ?? '';
  if (!first || first.toLowerCase() === mail.split('@')[0]) return null;
  const proper = first.charAt(0).toLocaleUpperCase('pl') + first.slice(1);
  return pick(NAME_FORMS[proper] ?? [proper]) ?? proper;
}

/** „Cześć, {name}!” → „Cześć, Julka!”, a bez imienia po prostu „Cześć!”. */
function fillGreeting(template: string, name: string | null): string {
  return name ? template.replace('{name}', name) : template.replace(/,? \{name\}/, '');
}

function pickMessage(role: UserRole, email: string, recent: readonly number[]) {
  const now = new Date();
  const weekday = now.getDay(); // 0 = niedziela
  const month = now.getMonth() + 1;
  const mail = email.toLowerCase();
  const fits = (m: WelcomeMessage) =>
    (!m.roles || m.roles.includes(role)) &&
    (!m.weekdays || m.weekdays.includes(weekday)) &&
    (!m.months || m.months.includes(month)) &&
    (!m.forEmails || m.forEmails.some((e) => e.toLowerCase() === mail));
  const candidates = WELCOME_MESSAGES.map((m, index) => ({ m, index })).filter(({ m }) => fits(m));
  const fresh = candidates.filter(({ index }) => !recent.includes(index));
  const personal = fresh.filter(({ m }) => m.forEmails);
  const pool = personal.length > 0 && Math.random() < PERSONAL_CHANCE ? personal : fresh;
  return pick(pool.length > 0 ? pool : candidates) ?? null;
}

export function buildWelcome(user: WelcomeUser): Welcome {
  const recent = readJson<Stored>(STORAGE_KEY)?.recent ?? [];
  const chosen = pickMessage(user.role, user.email, recent);
  const template = pick(GREETINGS_FOR[user.email.toLowerCase()] ?? GREETINGS) ?? 'Cześć, {name}!';
  return {
    userId: user.id,
    greeting: fillGreeting(template, displayName(user)),
    message: chosen?.m.text ?? null,
    messageIndex: chosen?.index ?? null,
  };
}
